import React, { useState, useCallback, useEffect } from 'react';
import { createStore } from 'redux';
import { Provider } from 'react-redux';
import { applyMiddleware } from 'helpers/redux_dev_tools';
import thunk from 'redux-thunk';
import PropTypes from 'prop-types';
import { Dropdown, Modal } from 'react-bootstrap-5';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';

import NewPropertyFromExisting from './new_listing_modal/new_property_from_existing';
import NewPropertyFromScratch from './new_listing_modal/new_property_from_scratch';
import DocumentsUploadModal from './new_listing_modal/documents_upload_modal';
import ProcessingToast from './new_listing_modal/processing_toast';
import ReviewListingDetailsModal from './new_listing_modal/review_listing_details_modal';
import reducers from './new_listing_modal/reducers';
import { normalizeBrokerOptions } from './new_listing_modal/helpers/normalizers';
import { countryListPropType, brokerTuple } from './new_listing_modal/prop_types';
import { useMultiDocUpload, resubscribeToIngestReview } from './new_listing_modal/use_multi_doc_upload';
import { mergeDocumentResults } from './new_listing_modal/merge_document_results';

import snakecaseKeys from 'snakecase-keys';

import { post, get, deleteRequest } from 'helpers/api';
import {
  maxHeightModal,
} from './new_listing_modal/styles.module.scss';

export default function NewListingModalReduxWrapper({
  allowUploadDoc,
  countryList,
  crmAccess,
  defaultBrokerId,
  defaultCountryId,
  edgeCrmAccess,
  showCreateDocButton,
  hasInsights,
  maxBrokersLimit,
  propertyTypes,
  brokerOptionTuples,
  pendingIngestReviews,
}) {
  const initialStoreData = {
    brokerSelectOptions: normalizeBrokerOptions(brokerOptionTuples),
    propertyTypes: propertyTypes,
    newPropertyFromScratchLocation: {
      country: countryList.find(country => country.id === defaultCountryId) ||
        countryList.find(country => country.name === 'United States') ||
        countryList[0]
    }
  };

  const store = createStore(reducers, initialStoreData, applyMiddleware(thunk));
  if (module.hot) {
    module.hot.accept('./new_listing_modal/reducers/index', () => {
      store.replaceReducer(require('./new_listing_modal/reducers/index'));
    });
  }

  return (
    <Provider store={store}>
      <NewListingModal
        allowUploadDoc={allowUploadDoc}
        countryList={countryList}
        crmAccess={crmAccess}
        defaultBrokerId={defaultBrokerId}
        defaultCountryId={defaultCountryId}
        edgeCrmAccess={edgeCrmAccess}
        hasInsights={hasInsights}
        maxBrokersLimit={maxBrokersLimit}
        pendingIngestReviews={pendingIngestReviews}
        showCreateDocButton={showCreateDocButton}
      />
    </Provider>
  );
}

// ── Doc flow step type ──────────────────────────────────────────────────────
// null     → no doc flow active
// 'upload' → DocumentsUploadModal is open
// 'processing' → upload modal closed, ProcessingToast is visible
// 'review' → ReviewListingDetailsModal is open
// ───────────────────────────────────────────────────────────────────────────

export function NewListingModal({
  allowUploadDoc,
  countryList,
  crmAccess,
  defaultBrokerId,
  defaultBrokerLabel,
  edgeCrmAccess,
  showCreateDocButton,
  hasInsights,
  maxBrokersLimit,
  pendingIngestReviews,
}) {
  const MODAL_STEPS = {
    FROM_SCRATCH: 'from_scratch',
    FROM_PROPERTY: 'from_property',
  };

  const hasAnyCrmAccess = crmAccess || edgeCrmAccess;

  // ── Scratch / from-existing modal state ───────────────────────────────────
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(MODAL_STEPS.FROM_SCRATCH);
  const [saving, setSaving] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});

  // ── Doc flow state ────────────────────────────────────────────────────────
  const [docFlowStep, setDocFlowStep] = useState(null);
  const [mergeResult, setMergeResult] = useState(null);
  const [docSaving, setDocSaving] = useState(false);
  const [docSaveError, setDocSaveError] = useState(null);

  // ── Multi-doc hook — lives here so it survives the upload modal closing ───

  const handleMultiDocComplete = useCallback((results, ingestReviewId) => {
    setMergeResult(mergeDocumentResults(results, ingestReviewId));
    // Refresh the grid so the placeholder card flips from pending → ready
    document.getElementById('property_search')?.requestSubmit();
  }, []);

  const handleProcessingStarted = useCallback(() => {
    // Refresh the grid only after the IngestReview record is confirmed created in the DB
    document.getElementById('property_search')?.requestSubmit();
  }, []);

  const multiDoc = useMultiDocUpload(handleMultiDocComplete, handleProcessingStarted);

  // ── Re-subscribe to any pending ingest reviews on mount (page reload) ─────
  useEffect(() => {
    if (!pendingIngestReviews || pendingIngestReviews.length === 0) return;

    const cleanups = pendingIngestReviews.map(({ sessionUuid }) => resubscribeToIngestReview(
      sessionUuid, (results, ingestReviewId) => {
        setMergeResult(mergeDocumentResults(results, ingestReviewId));
        // Refresh the grid so the card flips from pending → ready
        document.getElementById('property_search')?.requestSubmit();
      },
      () => {} // silently ignore error — card will remain in pending state
    )
    );

    return () => cleanups.forEach(fn => fn());
  }, []); // intentionally omit deps — only re-run on mount

  // ── Listen for card clicks from server-rendered ingest review cards ───────
  useEffect(() => {
    const openReview = async (ingestReviewId) => {
      try {
        const data = await get(`/projects/ingest_reviews/${ingestReviewId}`);
        // helpers/api deep-camelCases the response, but propertyFormData keys
        // must stay snake_case to match the live broadcast path (and the
        // ACTION_REQUIRED_FIELDS lookup in the review modal).
        const normalizedResults = (data.rawResults ?? []).map(result => ({
          ...result,
          data: result.data
            ? { ...result.data, propertyFormData: snakecaseKeys(result.data.propertyFormData || {}) }
            : result.data,
        }));
        setMergeResult(mergeDocumentResults(normalizedResults, ingestReviewId));
        setDocFlowStep('review');
      } catch {
        // no-op: card remains in ready state, user can click again
      }
    };

    const eventHandler = ({ detail: { ingestReviewId } }) => openReview(ingestReviewId);

    const clickHandler = (e) => {
      const card = e.target.closest('.js-ingest-review-card[data-status="ready"]');
      if (!card) return;
      const id = parseInt(card.dataset.ingestReviewId, 10);
      if (id) openReview(id);
    };

    window.addEventListener('openIngestReview', eventHandler);
    document.addEventListener('click', clickHandler);
    return () => {
      window.removeEventListener('openIngestReview', eventHandler);
      document.removeEventListener('click', clickHandler);
    };
  }, []);

  // ── Scratch / from-existing handlers ─────────────────────────────────────

  const openFromScratch = () => {
    setStep(MODAL_STEPS.FROM_SCRATCH);
    setHasErrors(false);
    setErrorMessages({});
    setShow(true);
  };

  const openFromProperty = () => {
    setStep(MODAL_STEPS.FROM_PROPERTY);
    setHasErrors(false);
    setErrorMessages({});
    setShow(true);
  };

  const closeMainModal = () => {
    setShow(false);
    setHasErrors(false);
    setErrorMessages({});
  };

  const brokersAttributes = (brokerIds) => {
    if (defaultBrokerId) {
      return { '0': { primary: true, user_id: defaultBrokerId } };
    }
    return brokerIds.map((brokerId, index) => ({
      primary: index === 0,
      sortOrder: index,
      userId: brokerId,
    }));
  };

  const save = ({
    edgeResearchPropertyId,
    researchPropertyId,
    brokerIds = [{}],
    propertyParams = {},
    researchPropertyParams = {},
    aiFeedback,
    aiIngestData,
    aiContentFlags,
    insightsId,
    createDocument,
  }) => {
    setSaving(true);
    setErrorMessages({});
    setHasErrors(false);

    const params = {
      edgeResearchPropertyId,
      researchPropertyId,
      aiFeedback,
      aiIngestData,
      aiContentFlags,
      createDocument,
      property: {
        draft: createDocument ? false : true,
        saleAgreementAttributes: { proposal: true },
        leaseAgreementAttributes: { proposal: true },
        projectsUsersAttributes: brokersAttributes(brokerIds),
        ...propertyParams,
        research_property_attributes: researchPropertyParams,
      },
    };

    post('/properties', params)
      .then(({ path }) => {
        const url = new URL(path, window.location.origin);
        if (insightsId) url.searchParams.set('address_correction', insightsId);
        window.location = url.toString();
      })
      .catch((error) => {
        setSaving(false);
        setHasErrors(true);
        setErrorMessages(error);
      });
  };

  const newPropertyScreen = (NewPropertyScreen, extraProps = {}) => {
    const newListingProps = {
      gotoPrevStep: null,
      saving,
      showBrokerSelect: !defaultBrokerId,
      onSave: save,
      hasErrors,
      errorMessages,
      maxBrokersLimit,
    };
    return <NewPropertyScreen {...newListingProps} {...extraProps} />;
  };

  const modalContent = () => {
    if (step === MODAL_STEPS.FROM_PROPERTY) {
      return newPropertyScreen(NewPropertyFromExisting, { edgeCrmAccess });
    }
    return newPropertyScreen(NewPropertyFromScratch, {
      countryList,
      defaultBrokerLabel,
      showCreateDocButton,
      hasInsights,
    });
  };

  // ── Doc flow handlers ─────────────────────────────────────────────────────

  const openFromDocs = () => {
    multiDoc.reset();
    setMergeResult(null);
    setDocSaveError(null);
    setDocFlowStep('upload');
  };

  const handleUploadModalHide = () => {
    multiDoc.reset();
    setDocFlowStep(null);
  };

  const handleStartProcessing = () => {
    // Close the upload modal immediately; toast takes over from here
    setDocFlowStep('processing');
    multiDoc.processFiles(multiDoc.docs);
    // Grid refresh happens in handleProcessingStarted after the DB record is confirmed created
  };

  const handleToastReviewReady = () => {
    setDocFlowStep('review');
  };

  const handleToastDismiss = () => {
    multiDoc.reset();
    setMergeResult(null);
    setDocFlowStep(null);
  };

  const handleReviewModalHide = () => {
    setDocFlowStep(null);
    setDocSaveError(null);
  };

  const handleDeleteIngestReview = useCallback(async () => {
    if (!mergeResult?.ingestReviewId) return;
    try {
      await deleteRequest(`/projects/ingest_reviews/${mergeResult.ingestReviewId}`);
      setDocFlowStep(null);
      setDocSaveError(null);
      setMergeResult(null);
      document.getElementById('property_search')?.requestSubmit();
    } catch {
      // silently fail — modal stays open so the user can try again
    }
  }, [mergeResult?.ingestReviewId]);

  const saveFromDocs = ({
    propertyParams, researchPropertyParams, brokerIds, createDocument, aiContentFlags, insightsId,
  }) => {
    if (!mergeResult) return;

    setDocSaving(true);
    setDocSaveError(null);

    const aiIngestData = {
      ai_extracted_fields: mergeResult.mergedFormData,
      inferred_fields: mergeResult.inferredFields,
      filename: mergeResult.allIngestMetadata.map(m => m.filename).join(', '),
      upload_id: mergeResult.allIngestMetadata[0]?.upload_id,
      ai_model: mergeResult.allIngestMetadata[0]?.ai_model,
    };

    const params = {
      aiFeedback: undefined,
      aiIngestData,
      aiContentFlags,
      createDocument,
      ingest_review_id: mergeResult.ingestReviewId,
      property: {
        draft: createDocument ? false : true,
        saleAgreementAttributes: { proposal: true },
        leaseAgreementAttributes: { proposal: true },
        projectsUsersAttributes: brokersAttributes(brokerIds),
        ...propertyParams,
        research_property_attributes: researchPropertyParams,
      },
    };

    post('/properties', params)
      .then(({ path }) => {
        const url = new URL(path, window.location.origin);
        if (insightsId) url.searchParams.set('address_correction', insightsId);
        window.location = url.toString();
      })
      .catch((error) => {
        setDocSaving(false);
        setDocSaveError(
          typeof error === 'string' ? error : 'An error occurred. Please try again.'
        );
      });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Trigger dropdown ─────────────────────────────────────────────── */}
      <Dropdown>
        <Dropdown.Toggle
          as="button"
          className="w-100 btn btn-primary d-flex align-items-center justify-content-center gap-2"
          name="new-listing-button"
          type="button"
        >
          <FontAwesomeIcon icon={faCirclePlus} />
          New Listing
          <FontAwesomeIcon icon={faCaretDown} />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {hasAnyCrmAccess && (
            <Dropdown.Item name="from-existing" onClick={openFromProperty}>
              From an Existing Property
            </Dropdown.Item>
          )}
          <Dropdown.Item name="from-scratch" onClick={openFromScratch}>
            From Scratch
          </Dropdown.Item>
          {allowUploadDoc && (
            <Dropdown.Item name="from-documents" onClick={openFromDocs}>
              From Documents
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {/* ── From scratch / from existing modal ───────────────────────────── */}
      <Modal
        dialogClassName={maxHeightModal}
        show={show}
        style={{ '--animate-duration': '0.3s' }}
        onHide={closeMainModal}
      >
        <SwitchTransition mode="out-in">
          <CSSTransition
            classNames={{
              enter: 'animate__animated',
              enterActive: 'animate__fadeIn',
              exit: 'animate__animated',
              exitActive: 'animate__fadeOut',
            }}
            key={step}
            timeout={500}
          >
            {modalContent()}
          </CSSTransition>
        </SwitchTransition>
      </Modal>

      {/* ── From documents — step 1: file picker ─────────────────────────── */}
      <DocumentsUploadModal
        canAddMore={multiDoc.canAddMore}
        docs={multiDoc.docs}
        fileDropError={multiDoc.fileDropError}
        getInputProps={multiDoc.getInputProps}
        getRootProps={multiDoc.getRootProps}
        isDragActive={multiDoc.isDragActive}
        removeDoc={multiDoc.removeDoc}
        show={docFlowStep === 'upload'}
        onHide={handleUploadModalHide}
        onStartProcessing={handleStartProcessing}
      />

      {/* ── From documents — step 2: background processing toast ─────────── */}
      {docFlowStep === 'processing' && (
        <ProcessingToast
          batchError={multiDoc.batchError}
          batchStatus={multiDoc.batchStatus}
          fileCount={multiDoc.docs.filter(d => d.status !== 'upload_error').length}
          hasConflicts={(mergeResult?.conflicts?.length ?? 0) > 0}
          onDismiss={handleToastDismiss}
          onReviewReady={handleToastReviewReady}
        />
      )}

      {/* ── From documents — step 3: review & submit ─────────────────────── */}
      <ReviewListingDetailsModal
        countryList={countryList}
        defaultBrokerId={defaultBrokerId}
        hasInsights={hasInsights}
        key={mergeResult?.ingestReviewId ?? 'new'}
        maxBrokersLimit={maxBrokersLimit}
        mergeResult={mergeResult}
        saveError={docSaveError}
        saving={docSaving}
        show={docFlowStep === 'review'}
        showCreateDocButton={showCreateDocButton}
        onConfirm={saveFromDocs}
        onDelete={handleDeleteIngestReview}
        onHide={handleReviewModalHide}
      />
    </>
  );
}

const sharedProps = {
  allowUploadDoc: PropTypes.bool,
  countryList: countryListPropType.isRequired,
  crmAccess: PropTypes.bool.isRequired,
  defaultBrokerId: PropTypes.number,
  defaultBrokerLabel: PropTypes.string,
  edgeCrmAccess: PropTypes.bool,
  hasInsights: PropTypes.bool.isRequired,
  maxBrokersLimit: PropTypes.number.isRequired,
  pendingIngestReviews: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    sessionUuid: PropTypes.string.isRequired,
  })),
  showCreateDocButton: PropTypes.bool,
};

NewListingModalReduxWrapper.propTypes = {
  ...sharedProps,
  brokerOptionTuples: PropTypes.arrayOf(brokerTuple).isRequired,
  defaultCountryId: PropTypes.number,
  propertyTypes: PropTypes.objectOf(PropTypes.string),
};

NewListingModal.propTypes = sharedProps;
